/**
 * SOFSEK — Remediation Workspace (§5, §8 Pathway B — Atomic Action Eviction).
 * A sandbox session stages real deltas against real tables (never a "memory"
 * cache); "New Eviction" builds the flagship Pathway B flow — pick a role +
 * permission to strip, see the blast radius (every OTHER subject who would
 * lose it) before staging anything. Validate runs a genuine dry-run through
 * the Common Routine + real conflict scan, then rolls back (sfk_validate_
 * sandbox_session) — production is untouched until Commit, which is blocked
 * by the promotion gate until every violation found is dispositioned.
 */
import { Fragment, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiDb } from '../../lib/api_client';
import { routedInsert, routedUpdate } from '../../lib/routed_writes';
import { getForensicActorId } from '../../types/auth';
import { hasAccess, type AuthSession } from '../../types/auth';
import { Modal } from '../../components/common/Modal';
import { EmptyState } from '../../components/common/EmptyState';
import { RelatedChildrenPanel, RelatedExpandButton, useExpandedRows, type RelatedChildGroup } from '../../components/common/RelatedChildrenPanel';
import { FacetFilter } from '../../components/common/FacetFilter';
import { ClearFiltersButton } from '../../components/common/ClearFiltersButton';
import { useFormatDateTime } from '../../lib/format_date';
import { PageHeader } from '../../components/common/PageHeader';
import { StatStrip } from '../../components/common/StatStrip';
import { ContentCard } from '../../components/common/ContentCard';
import { RefreshCw, Plus } from 'lucide-react';
import { PersonalizationProvider, PersonalizationToggle, PersonalizedGrid, PersonalizedField, GRID_TABLE_CLASS } from '../../components/personalization';
import type { GridColumnDef } from '../../components/personalization';

const PAGE_CODE = 'SFK.SANDBOX_SESSION.LIST';
const inputCls = 'mb-3 h-8 w-full rounded border border-csm-border px-2 text-csm-sm';
const labelCls = 'mb-1 block text-csm-sm font-medium text-csm-ink-soft';
const btnCls = 'h-7 rounded border border-csm-border bg-white px-2 text-csm-sm hover:bg-csm-surface-alt';

interface CodeOpt { code: string; name: string; }
interface SessionRow {
  session_id: number;
  internal_code: string;
  sandbox_status_code: string;
  created_at: string;
}

function tlName(list: unknown, fallback: string): string {
  const arr = (list ?? []) as { locale_code: string; display_text: string }[];
  return arr.find(x => x.locale_code === 'EN_US')?.display_text ?? fallback;
}

const GRID_COLS: GridColumnDef[] = [
  { key: 'internal_code', header: 'Session', defaultWidth: 200 },
  { key: 'status', header: 'Status', defaultWidth: 140 },
  { key: 'created_at', header: 'Created', defaultWidth: 170 },
];

function SandboxInner({ session }: { session: AuthSession }) {
  const { t } = useTranslation();
  const formatDateTime = useFormatDateTime();
  const [sessions, setSessions] = useState<SessionRow[]>([]);
  const [statuses, setStatuses] = useState<CodeOpt[]>([]);
  const [deltas, setDeltas] = useState<{ delta_id: number; session_id: number; description: string | null }[]>([]);
  const [statusFacet, setStatusFacet] = useState<string[]>([]);
  const [selected, setSelected] = useState<SessionRow | null>(null);
  const [creatingEviction, setCreatingEviction] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = async () => {
    setBusy(true); setError(null);
    try {
      const [sessRes, statusRes, deltaRes] = await Promise.all([
        apiDb.from('sfk_sandbox_session').select('session_id, internal_code, sandbox_status_code, created_at').order('created_at', { ascending: false }),
        apiDb.from('sfk_sandbox_status').select('sandbox_status_code, sfk_sandbox_status_tl ( locale_code, display_text )'),
        apiDb.from('sfk_sandbox_delta').select('delta_id, session_id, description'),
      ]);
      if (sessRes.error) throw sessRes.error;
      if (statusRes.error) throw statusRes.error;
      if (deltaRes.error) throw deltaRes.error;
      setSessions((sessRes.data ?? []) as unknown as SessionRow[]);
      setStatuses(((statusRes.data ?? []) as unknown as Record<string, unknown>[]).map(r => ({ code: r.sandbox_status_code as string, name: tlName(r.sfk_sandbox_status_tl, r.sandbox_status_code as string) })));
      setDeltas((deltaRes.data ?? []) as unknown as { delta_id: number; session_id: number; description: string | null }[]);
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };

   
  useEffect(() => { void reload(); }, []);

  const resolveNameForStatus = (code: string) => statuses.find(s => s.code === code)?.name ?? code;
  const deltasFor = (sessionId: number) => deltas.filter(d => d.session_id === sessionId);

  const detail = useExpandedRows<number>();
  // §6/§17: csm_role_page_access decides which write affordances this user is
  // offered. The server refuses the write regardless (RLS, via the Common
  // Routine) — this stops the page offering an action that can only error.
  const canAdd    = hasAccess(session, PAGE_CODE, 'add');
  // §17.B13 — a sandbox session's related children are its staged deltas.
  const buildRelatedGroups = (s: SessionRow): RelatedChildGroup[] => [
    { key: 'deltas', title: t('sfk.sandbox.related.deltas', 'Staged Changes'), items: deltasFor(s.session_id).map(d => ({
      id: d.delta_id,
      label: d.description ?? t('sfk.sandbox.delta_fallback', `Delta #${d.delta_id}`),
    })) },
  ];
  const hasRelated = (s: SessionRow) => deltasFor(s.session_id).length > 0;

  const filtered = useMemo(() => sessions.filter(s => statusFacet.length === 0 || statusFacet.includes(s.sandbox_status_code)), [sessions, statusFacet]);

  return (
    <div className="flex h-full flex-col">
      <div className="flex min-h-0 flex-1 flex-col gap-[18px] overflow-auto p-6">
        <PageHeader
          title={t('sfk.sandbox.title', 'Remediation Workspace')}
          description={t('sfk.sandbox.subtitle', 'Stage, validate and commit remediation deltas — production stays untouched until commit.')}
          actions={
            <>
              <button onClick={() => void reload()} className="btn-secondary flex items-center gap-1.5"><RefreshCw className="h-3.5 w-3.5" /> {t('common.refresh', 'Refresh')}</button>
              {canAdd && (<button onClick={() => setCreatingEviction(true)} className="btn-primary flex items-center gap-1.5"><Plus className="h-3.5 w-3.5" /> {t('sfk.sandbox.new_eviction', 'New Eviction (Pathway B)')}</button>)}
            </>
          }
        />
        <StatStrip stats={[{ value: sessions.length, label: t('common.total', 'Total') }, { value: filtered.length, label: t('common.shown', 'Shown') }]} />
        {error && <div className="rounded-csm-card border border-csm-danger-border bg-csm-danger-soft px-3 py-1.5 text-csm-xs text-csm-danger">{error}</div>}
        <ContentCard className="flex min-h-0 flex-1 flex-col">
          <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-csm-line bg-white px-3 py-2">
            <FacetFilter label={t('sfk.sandbox.facet.status', 'Status')} options={statuses.map(s => ({ value: s.code, label: s.name }))} selected={statusFacet} onChange={setStatusFacet} />
            <ClearFiltersButton active={statusFacet.length > 0} onClear={() => setStatusFacet([])} />
            <div className="ml-auto flex items-center gap-2">
              {busy && <span className="text-csm-xs text-csm-muted">{t('common.loading', 'Loading…')}</span>}
              <PersonalizationToggle />
            </div>
          </div>
          <div className="flex-1 overflow-auto">
            <PersonalizedGrid componentKey="grid" columns={GRID_COLS}>
          {(visibleCols, widths) => (
            <table className={GRID_TABLE_CLASS}>
              <thead className="sticky top-0 z-10 bg-csm-surface-alt text-csm-xs uppercase tracking-wider text-csm-ink-soft">
                <tr>
                  <th className="border-b border-csm-border px-1 py-1.5 text-left" style={{ width: '1%' }} aria-hidden>&nbsp;</th>
                  {visibleCols.map(col => (
                    <th key={col.key} className="border-b border-csm-border px-3 py-1.5 text-left" style={widths[col.key] ? { width: widths[col.key] } : undefined}>{col.header}</th>
                  ))}
                  <th className="border-b border-csm-border px-3 py-1.5 text-left" style={{ width: '1%' }}>{t('common.actions', 'Actions')}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(s => (
                  <Fragment key={s.session_id}>
                  <tr className="hover:bg-csm-surface-alt">
                    <td className="border-b border-csm-border-soft px-1 py-1.5 text-center align-middle">
                      <RelatedExpandButton open={detail.isOpen(s.session_id)} hasChildren={hasRelated(s)} onToggle={() => detail.toggle(s.session_id)}
                        expandLabel={t('common.related.expand', 'Show related records')} collapseLabel={t('common.related.collapse', 'Hide related')} tooltip={t('common.related.tooltip', 'Show related records')} />
                    </td>
                    {visibleCols.map(col => (
                      <td key={col.key} className="border-b border-csm-border-soft px-3 py-1.5" style={widths[col.key] ? { width: widths[col.key] } : undefined}>
                        {col.key === 'internal_code' && <span className="font-mono text-csm-sm">{s.internal_code}</span>} {/* fk-display-ok: session's own reference code */}
                        {col.key === 'status' && (
                          <span className={`rounded px-1.5 py-0.5 text-csm-xs ${s.sandbox_status_code === 'COMMITTED' ? 'bg-csm-success-soft text-csm-success' : s.sandbox_status_code === 'DISCARDED' ? 'bg-csm-surface-alt text-csm-watermark' : 'bg-csm-warning-soft text-csm-warning'}`}>
                            {resolveNameForStatus(s.sandbox_status_code)}
                          </span>
                        )}
                        {col.key === 'created_at' && formatDateTime(s.created_at)}
                      </td>
                    ))}
                    <td className="border-b border-csm-border-soft px-3 py-1.5 text-right">
                      <button onClick={() => setSelected(s)} className="rounded border border-csm-border bg-white px-2 py-0.5 text-csm-xs hover:bg-csm-border-soft">{t('sfk.sandbox.manage', 'Manage')}</button>
                    </td>
                  </tr>
                  {detail.isOpen(s.session_id) && hasRelated(s) && (
                    <tr>
                      <td colSpan={visibleCols.length + 2} className="border-b border-csm-border-soft bg-csm-surface-alt/40 px-3 py-2">
                        <RelatedChildrenPanel groups={buildRelatedGroups(s)} emptyLabel={t('common.related.none', 'No related records')} />
                      </td>
                    </tr>
                  )}
                  </Fragment>
                ))}
                {filtered.length === 0 && !busy && (
                  <tr><td colSpan={visibleCols.length + 2} className="py-12 text-center"><EmptyState title={t('common.no_records', 'No records')} /></td></tr>
                )}
              </tbody>
            </table>
          )}
            </PersonalizedGrid>
          </div>
        </ContentCard>
      </div>

      {creatingEviction && (
        <NewEvictionModal session={session}
          onClose={() => setCreatingEviction(false)}
          onSaved={() => { setCreatingEviction(false); void reload(); }} />
      )}
      {selected && (
        <SessionManageModal session={session} row={selected}
          onClose={() => setSelected(null)}
          onChanged={() => void reload()} />
      )}
    </div>
  );
}

function NewEvictionModal({ session, onClose, onSaved }: { session: AuthSession; onClose: () => void; onSaved: () => void }) {
  const { t } = useTranslation();
  const [roles, setRoles] = useState<CodeOpt[]>([]);
  const [permissions, setPermissions] = useState<CodeOpt[]>([]);
  const [roleId, setRoleId] = useState('');
  const [permissionId, setPermissionId] = useState('');
  const [blastRadius, setBlastRadius] = useState<{ app_user_id: number; name: string }[] | null>(null);
  const [checkingBlast, setCheckingBlast] = useState(false);
  const [sessionCode, setSessionCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      const [roleRes, permRes] = await Promise.all([
        apiDb.from('sfk_external_role').select('role_id, name'),
        apiDb.from('sfk_v_action').select('permission_id, display_name'),
      ]);
      setRoles(((roleRes.data ?? []) as { role_id: number; name: string }[]).map(r => ({ code: String(r.role_id), name: r.name })));
      setPermissions(((permRes.data ?? []) as { permission_id: number; display_name: string }[]).map(p => ({ code: String(p.permission_id), name: p.display_name })));
    })();
  }, []);

  const checkBlastRadius = async () => {
    if (!roleId || !permissionId) return;
    setCheckingBlast(true); setError(null); setBlastRadius(null);
    try {
      const { data, error: e } = await apiDb.rpc('sfk_role_permission_blast_radius', { p_role_id: Number(roleId), p_permission_id: Number(permissionId) });
      if (e) throw e;
      const subjectIds = ((data ?? []) as { app_user_id: number }[]).map(r => r.app_user_id);
      if (subjectIds.length) {
        const { data: subjData } = await apiDb.from('csm_user_master').select('user_id, display_name').in('user_id', subjectIds);
        setBlastRadius(((subjData ?? []) as { user_id: number; display_name: string }[]).map(s => ({ app_user_id: s.user_id, name: s.display_name })));
      } else {
        setBlastRadius([]);
      }
    } catch (e) { setError((e as Error).message); } finally { setCheckingBlast(false); }
  };

  const stage = async () => {
    setBusy(true); setError(null);
    try {
      if (!sessionCode.trim()) throw new Error(t('sfk.sandbox.err.code_required', 'A session code is required.'));
      if (!roleId || !permissionId) throw new Error(t('sfk.sandbox.err.selection_required', 'Select a role and permission.'));

      // A role's grant of a function is a can_view row on the unified access
      // table (roles folded into csm_role_master — migration 20260901000000).
      const { data: rpRow, error: rpErr } = await apiDb.from('csm_role_page_access').select('role_page_access_id').eq('role_id', Number(roleId)).eq('page_id', Number(permissionId)).eq('can_view', true).limit(1);
      if (rpErr) throw new Error(rpErr.message);
      const mapping = ((rpRow ?? []) as { role_page_access_id: number }[])[0];
      if (!mapping) throw new Error(t('sfk.sandbox.err.mapping_not_found', 'This role does not currently grant that permission.'));

      const { row_id, error: e } = await routedInsert(session, 'sfk_sandbox_session', {
        internal_code: sessionCode.trim().toUpperCase(),
        created_by: getForensicActorId(session),
      });
      if (e) throw new Error(e.message);
      const sessionId = row_id;
      if (sessionId != null) {
        const roleName = roles.find(r => r.code === roleId)?.name ?? roleId;
        const permName = permissions.find(p => p.code === permissionId)?.name ?? permissionId;
        const { error: e2 } = await routedInsert(session, 'sfk_sandbox_delta', {
          session_id: sessionId,
          target_table: 'csm_role_page_access',
          sandbox_operation_code: 'DELETE',
          payload: { role_page_access_id: mapping.role_page_access_id },
          key_columns: ['role_page_access_id'],
          description: `Evict "${permName}" from role "${roleName}" — affects ${blastRadius?.length ?? '?'} other subject(s).`,
        });
        if (e2) throw new Error(e2.message);
      }
      onSaved();
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };

  return (
    <Modal
      title={t('sfk.sandbox.new_eviction', 'New Eviction (Pathway B)')}
      onClose={onClose}
      width="md"
      headerAction={<PersonalizationToggle />}
      footer={
        <>
          <button onClick={onClose} className="rounded border border-csm-border bg-white px-3 py-1 text-csm-sm hover:bg-csm-border-soft">{t('common.cancel', 'Cancel')}</button>
          <button onClick={() => void stage()} disabled={busy || !blastRadius} className="rounded border border-primary bg-primary px-3 py-1 text-csm-sm text-white hover:bg-primary-hover disabled:opacity-60">{busy ? t('common.saving', 'Staging…') : t('sfk.sandbox.stage', 'Stage in Sandbox')}</button>
        </>
      }
    >
      <PersonalizedField componentKey="modal" fieldKey="role_id" label={t('sfk.sandbox.role', 'Role')}>
        <label className={labelCls}>{t('sfk.sandbox.role', 'Role')}</label>
        <select value={roleId} onChange={e => { setRoleId(e.target.value); setBlastRadius(null); }} className={`${inputCls} bg-white`}>
          <option value="">{t('sfk.sandbox.select_role', '— select role —')}</option>
          {roles.map(r => <option key={r.code} value={r.code}>{r.name}</option>)}
        </select>
      </PersonalizedField>
      <PersonalizedField componentKey="modal" fieldKey="permission_id" label={t('sfk.sandbox.permission', 'Permission to Evict')}>
        <label className={labelCls}>{t('sfk.sandbox.permission', 'Permission to Evict')}</label>
        <select value={permissionId} onChange={e => { setPermissionId(e.target.value); setBlastRadius(null); }} className={`${inputCls} bg-white`}>
          <option value="">{t('sfk.sandbox.select_permission', '— select permission —')}</option>
          {permissions.map(p => <option key={p.code} value={p.code}>{p.name}</option>)}
        </select>
      </PersonalizedField>
      <button onClick={() => void checkBlastRadius()} disabled={!roleId || !permissionId || checkingBlast} className={`${btnCls} mb-3`}>
        {checkingBlast ? t('sfk.sandbox.checking', 'Checking…') : t('sfk.sandbox.check_blast_radius', 'Check Blast Radius')}
      </button>

      {blastRadius !== null && (
        <div className="mb-3 rounded border border-csm-warning-border bg-csm-warning-soft px-2 py-1.5 text-csm-xs text-csm-warning">
          {blastRadius.length === 0
            ? t('sfk.sandbox.blast_radius_none', 'No other subjects hold this permission via this role.')
            : t('sfk.sandbox.blast_radius_warning', `Warning: removing this will immediately strip this capability from ${blastRadius.length} other subject(s):`)}
          {blastRadius.length > 0 && (
            <ul className="mt-1 max-h-24 list-disc overflow-auto pl-4">
              {blastRadius.map(b => <li key={b.app_user_id}>{b.name}</li>)}
            </ul>
          )}
        </div>
      )}

      <PersonalizedField componentKey="modal" fieldKey="internal_code" label={t('sfk.sandbox.session_code', 'Session Code')}>
        <label className={labelCls}>{t('sfk.sandbox.session_code', 'Session Code')}</label>
        <input value={sessionCode} onChange={e => setSessionCode(e.target.value.toUpperCase())} className={`${inputCls} font-mono`} placeholder="EVICT_2026_001" />
      </PersonalizedField>

      {error && <div className="mt-3 rounded border border-csm-danger-border bg-csm-danger-soft px-2 py-1 text-csm-sm text-csm-danger">{error}</div>}
    </Modal>
  );
}

function SessionManageModal({ session, row, onClose, onChanged }: { session: AuthSession; row: SessionRow; onClose: () => void; onChanged: () => void }) {
  // This modal opens on a read action ("Manage"), so it must gate its own
  // writes — the entry point cannot stand in for them here.
  const canChange = hasAccess(session, PAGE_CODE, 'change');
  const { t } = useTranslation();
  const [deltas, setDeltas] = useState<{ delta_id: number; description: string | null }[]>([]);
  const [latestResult, setLatestResult] = useState<{ result_id: number; new_violation_count: number; sandbox_disposition_code: string | null } | null>(null);
  const [reason, setReason] = useState('');
  const [dispositions, setDispositions] = useState<CodeOpt[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = async () => {
    const [deltaRes, resultRes, dispRes] = await Promise.all([
      apiDb.from('sfk_sandbox_delta').select('delta_id, description').eq('session_id', row.session_id),
      apiDb.from('sfk_sandbox_validation_result').select('result_id, new_violation_count, sandbox_disposition_code').eq('session_id', row.session_id).order('validated_at', { ascending: false }).limit(1),
      apiDb.from('sfk_sandbox_disposition').select('sandbox_disposition_code, sfk_sandbox_disposition_tl ( locale_code, display_text )'),
    ]);
    setDeltas((deltaRes.data ?? []) as { delta_id: number; description: string | null }[]);
    setLatestResult((((resultRes.data ?? []) as { result_id: number; new_violation_count: number; sandbox_disposition_code: string | null }[])[0]) ?? null);
    setDispositions(((dispRes.data ?? []) as unknown as Record<string, unknown>[]).map(r => ({ code: r.sandbox_disposition_code as string, name: tlName(r.sfk_sandbox_disposition_tl, r.sandbox_disposition_code as string) })));
  };
  const resolveNameForDisposition = (code: string) => dispositions.find(d => d.code === code)?.name ?? code;

  useEffect(() => { void reload(); }, [row.session_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const validate = async () => {
    setBusy(true); setError(null);
    try {
      const { data, error: e } = await apiDb.rpc('sfk_validate_sandbox_session', { p_session_id: row.session_id });
      if (e) throw e;
      const res = data as { ok?: boolean; error?: string };
      if (res?.ok === false) throw new Error(res.error);
      await reload(); onChanged();
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };

  const dispose = async (disposition: 'WITHDRAWN' | 'ACCEPTED') => {
    if (!latestResult) return;
    setBusy(true); setError(null);
    try {
      const { error: e } = await routedUpdate(session, 'sfk_sandbox_validation_result', 'result_id', latestResult.result_id, {
        sandbox_disposition_code: disposition, disposition_reason: reason.trim() || null,
        disposed_by: getForensicActorId(session), disposed_at: new Date().toISOString(),
      });
      if (e) throw new Error(e.message);
      await reload(); onChanged();
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };

  const commit = async () => {
    setBusy(true); setError(null);
    try {
      const { data, error: e } = await apiDb.rpc('sfk_commit_sandbox_session', { p_session_id: row.session_id });
      if (e) throw e;
      const res = data as { ok?: boolean; error?: string };
      if (res?.ok === false) throw new Error(res.error);
      onChanged(); onClose();
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };

  const discard = async () => {
    setBusy(true); setError(null);
    try {
      const { error: e } = await routedUpdate(session, 'sfk_sandbox_session', 'session_id', row.session_id, { sandbox_status_code: 'DISCARDED' });
      if (e) throw new Error(e.message);
      onChanged(); onClose();
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };

  const needsDisposition = latestResult && latestResult.new_violation_count > 0 && !latestResult.sandbox_disposition_code;

  return (
    <Modal
      title={`${t('sfk.sandbox.manage', 'Manage Session')} — ${row.internal_code}`}
      onClose={onClose}
      width="md"
      headerAction={<PersonalizationToggle />}
      footer={
        <>
          <button onClick={onClose} className="rounded border border-csm-border bg-white px-3 py-1 text-csm-sm hover:bg-csm-border-soft">{t('common.close', 'Close')}</button>
          {canChange && (<button onClick={() => void discard()} disabled={busy || row.sandbox_status_code !== 'DRAFT' && row.sandbox_status_code !== 'VALIDATED'} className="rounded border border-csm-danger bg-white px-3 py-1 text-csm-sm text-csm-danger hover:bg-csm-danger-soft disabled:opacity-60">{t('sfk.sandbox.discard', 'Discard')}</button>)}
          {canChange && (<button onClick={() => void commit()} disabled={busy || !latestResult || needsDisposition === true} className="rounded border border-primary bg-primary px-3 py-1 text-csm-sm text-white hover:bg-primary-hover disabled:opacity-60">{t('sfk.sandbox.commit', 'Accept & Commit to Live')}</button>)}
        </>
      }
    >
      <div className="mb-3">
        <div className="mb-1 text-csm-xs font-medium uppercase tracking-wider text-csm-watermark">{t('sfk.sandbox.staged_deltas', 'Staged Changes')}</div>
        {deltas.length === 0 ? <EmptyState title={t('common.no_records', 'No records')} /> : (
          <ul className="list-disc pl-4 text-csm-sm">
            {deltas.map(d => <li key={d.delta_id}>{d.description ?? `Delta #${d.delta_id}`}</li>)}
          </ul>
        )}
      </div>

      <button onClick={() => void validate()} disabled={busy} className={`${btnCls} mb-3`}>{busy ? t('sfk.sandbox.validating', 'Validating…') : t('sfk.sandbox.validate', 'Validate (dry-run)')}</button>

      {latestResult && (
        <div className={`mb-3 rounded border px-2 py-1.5 text-csm-sm ${latestResult.new_violation_count > 0 ? 'border-csm-danger-border bg-csm-danger-soft text-csm-danger' : 'border-csm-success-border bg-csm-success-soft text-csm-success'}`}>
          {latestResult.new_violation_count > 0
            ? t('sfk.sandbox.new_violations_found', `Predicted new violations: ${latestResult.new_violation_count}`)
            : t('sfk.sandbox.no_new_violations', 'No new violations predicted.')}
          {latestResult.sandbox_disposition_code && <div className="mt-1 text-csm-xs">{t('sfk.sandbox.dispositioned_as', 'Dispositioned')}: {resolveNameForDisposition(latestResult.sandbox_disposition_code)}</div>}
        </div>
      )}

      {needsDisposition && (
        <div className="mb-3">
          <label className={labelCls}>{t('sfk.sandbox.disposition_reason', 'Reason (required to accept anyway)')}</label>
          <textarea value={reason} onChange={e => setReason(e.target.value)} rows={2} className="mb-2 w-full resize-y rounded border border-csm-border px-2 py-1 text-csm-sm" />
          <div className="flex gap-2">
            <button onClick={() => void dispose('WITHDRAWN')} disabled={busy} className={btnCls}>{t('sfk.sandbox.withdraw', 'Withdraw Change')}</button>
            <button onClick={() => void dispose('ACCEPTED')} disabled={busy || !reason.trim()} className={btnCls}>{t('sfk.sandbox.accept_anyway', 'Accept With Justification')}</button>
          </div>
        </div>
      )}

      {error && <div className="mt-3 rounded border border-csm-danger-border bg-csm-danger-soft px-2 py-1 text-csm-sm text-csm-danger">{error}</div>}
    </Modal>
  );
}

export function SfkSandboxSessionPage({ session }: { session: AuthSession }) {
  return (
    <PersonalizationProvider pageCode={PAGE_CODE} session={session}>
      <SandboxInner session={session} />
    </PersonalizationProvider>
  );
}
